import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Autocomplete } from '@base-ui/react/autocomplete';
import { Field } from '@base-ui/react/field';
import { format, isValid, parse } from 'date-fns';

import { CaretDownIcon, XIcon } from '../Icon';
import { useInheritedDarkMode } from '../Theme';
import { cn } from '../utils';
import {
  atTimeOfDay,
  boundMinutes,
  isTimeSelectable,
  matchesTimeQuery,
  minutesOfDay,
  selectableTimeSlots,
  type TimeSlot,
} from './slots';
import {
  timePickerBoxRecipe,
  timePickerCaretRecipe,
  timePickerClearRecipe,
  timePickerControlRecipe,
  timePickerHelperRecipe,
  timePickerItemRecipe,
  timePickerLabelRecipe,
  timePickerRequiredMarkRecipe,
  timePickerListRecipe,
  timePickerPopupRecipe,
  timePickerPositionerRecipe,
  timePickerRootRecipe,
} from './TimePicker.recipe';
import { DEFAULT_TIME_PICKER_MESSAGES, TIME_PICKER_FORMAT, TIME_PICKER_PLACEHOLDER } from './types';

import type { TimePickerAccessibleLabels, TimePickerEntryError, TimePickerProps } from './types';

/**
 * The shape a typed entry has to have before it is worth parsing.
 *
 * `date-fns` is forgiving about digit counts in a way this field wants — `9:30 AM` reads as half
 * past nine, which is what a user typing quickly means. It is unforgiving about exactly one thing
 * this field does not want it to be: the literal space in `hh:mm a`. `9:30AM` fails to parse, and
 * `9:30AM` is precisely the form the LEGACY field prints in its own slot list, so the first thing a
 * user is likely to copy is the one thing the parser rejects. `MERIDIEM` below closes that.
 *
 * The guard also refuses a bare `9:30`, which `parse` rejects anyway, and a 24-hour `13:30 AM`,
 * which it also rejects — both deliberately, since this field owns a 12-hour format (KTD2) and
 * accepting a 24-hour entry would mean the field printed back something the user did not type.
 */
const SHORT_TIME = /^\d{1,2}:\d{1,2}\s*[ap]\.?m?\.?$/i;

/**
 * The trailing meridiem, however it was written, normalised to the single space `hh:mm a` needs.
 *
 * Runs only on a string `SHORT_TIME` has already matched, so it cannot mangle arbitrary text.
 * `9:30AM`, `9:30 am`, `9:30 p.m.` and `9:30 a` all become the two forms `parse` understands.
 */
const MERIDIEM = /\s*([ap])\.?m?\.?$/i;

/**
 * Minutes since midnight for a typed entry, or `null` when it is not a time.
 *
 * Returns MINUTES rather than a `Date` on purpose: the range check is a question about a time of
 * day (see `slots.ts`), and handing it a `Date` is how an instant comparison creeps back in. The
 * committed `Date` is built separately, from the caller's own value, by `atTimeOfDay`.
 */
const parseTimeOfDay = (entry: string): number | null => {
  if (!SHORT_TIME.test(entry)) {
    return null;
  }

  // Any date will do — only the time of day is read back out.
  const parsed = parse(entry.replace(MERIDIEM, ' $1m'), TIME_PICKER_FORMAT, new Date(2026, 0, 15, 12));

  return isValid(parsed) ? minutesOfDay(parsed) : null;
};

/** The committed value as text, which is the empty string for an empty field. */
const toText = (value: Date | null) => (value === null ? '' : format(value, TIME_PICKER_FORMAT));

/** Two committed values are the same when they are the same TIME, since that is all this field holds. */
const sameTime = (a: Date | null, b: Date | null) => {
  if (a === null || b === null) {
    return a === b;
  }

  return minutesOfDay(a) === minutesOfDay(b);
};

/**
 * English defaults for the two names only a screen reader hears, replaceable one at a time through
 * `accessibleLabels`.
 *
 * Both take the field's `label`, so two time fields in one row announce distinguishable controls
 * rather than two identical ones — which is exactly the arrangement the only call site has.
 */
const DEFAULT_ACCESSIBLE_LABELS: TimePickerAccessibleLabels = {
  clear: (label) => `Clear ${label}`,
  list: (label) => `${label} options`,
};

/**
 * Design System 2.0 time picker — an editable field that takes a time by typing or from a list of
 * 15-minute slots.
 *
 * ── KTD7: THERE IS NO FIGMA DRAWING FOR A TIME FIELD, AND EVERYTHING VISUAL HERE IS EXTRAPOLATED ─
 *
 * This is the first thing to know about this component and the reason it is stated first. The
 * Design System 2.0 library contains no time picker, in any state, on any page. HNS-418 says so,
 * and it was re-checked when this was built. So unlike `DatePicker` — which was ported from
 * `Date Picker` 1438:129727 and could be corrected against it cell by cell — nothing below has been
 * confirmed by a designer.
 *
 * What it was extrapolated FROM, so a reviewer can check the reasoning rather than the pixels:
 *
 *   the field half     `Date Picker` 1438:129727, itself built from Text Input "so the two stack
 *                      flush in one group". A time field is a text field, so it takes the same
 *                      60px box, the same floating label, the same accent focus stroke and the
 *                      same helper-inside-the-border treatment.
 *   the popup and rows `Menu` 556:20625 by way of `Select.recipe` — a slot IS an option row, so it
 *                      is drawn as one rather than as something new.
 *   the caret          `CaretDown`, the glyph every other list-opening control in the system uses.
 *
 * What is NOT drawn anywhere and is this component's own invention: the two right-gutter
 * affordances sitting side by side (no field in the library has two), the nine-row height cap on
 * the popup, and the decision to make the caret a glyph rather than a button.
 *
 * This follows the precedent `Select`'s `trailing` slot sets — marked EXTRAPOLATED in its own
 * types, with a standing request for a designer's confirmation. The same request stands here, it is
 * recorded on HNS-418, and it is a sign-off condition rather than a build one. A reviewer who finds
 * this wrong should expect to change `TimePicker.recipe.ts` and the `.ds-timepicker` block and
 * nothing else: no behaviour below depends on any of it.
 *
 * ── KTD4: `min` IS A TIME-OF-DAY BOUND, WHICH IS THE ONE DIVERGENCE FROM `DatePicker` ─────────
 *
 * `DatePicker` compares a typed entry against `startOfDay(min)`, so a mid-day `min` means the whole
 * of its day. Copying that here would make `min` do nothing at all — every slot this field offers
 * shares one day, so `startOfDay(min)` is at or before all 96 of them. The wizard's end-time field,
 * whose entire job is to forbid an end before its start, would silently stop forbidding anything.
 *
 * Comparing raw INSTANTS is also wrong, for a reason specific to the one call site: the wizard
 * hands the end field `addMinutes(startTime, 15)` while the value being edited sits on the
 * occurrence's own date, and the two `Date`s are not required to share a day.
 *
 * So `min` is compared BY TIME OF DAY and its calendar day is ignored. The rule, the two
 * consequences it has, and the one case it cannot express (a bound that has crossed midnight) are
 * written out in full beside the predicate in `slots.ts`. Read that before changing anything about
 * `min`; it is the part of this component most likely to be "fixed" back into doing nothing.
 *
 * ── One predicate, two ways in ────────────────────────────────────────────────────────────────
 *
 * A combobox has two entry paths and they must not be able to disagree about what is selectable.
 * The popup list is `selectableTimeSlots(boundMinutes(min))` — the generator filtered through the
 * predicate — and the blur parser calls the SAME predicate, on the same bound, with the minutes it
 * parsed. `boundMinutes` is the single place a `Date` becomes a time of day, so nothing downstream
 * is even capable of comparing instants. That is KTD4 made structural rather than remembered.
 *
 * ── Why Base UI's `Autocomplete` and not `DatePicker`'s input-plus-trigger pair ───────────────
 *
 * A combobox's keyboard and ARIA model is the part that is easy to get wrong and hard to notice:
 * `role="combobox"` on the input, `aria-expanded`, `aria-controls`, `aria-activedescendant` moving
 * with the highlight, Down-from-input, Escape-to-close, and focus returning to the input on commit.
 * The library implements all of it. The legacy field implements none of it — it is a `Popper` plus a
 * `MenuList` with no combobox roles at all — so R7 is met by adoption rather than by effort. This is
 * the same argument `DatePicker` used to adopt `react-day-picker` rather than hand-roll a grid.
 *
 * It is also ONE control and one tab stop, where `DatePicker` has two. That field needs a separate
 * calendar button because `react-day-picker` ships no input and a month grid is not a listbox; a
 * time list IS a listbox, so the compromise is not needed here and the caret is left as a glyph.
 *
 * `Autocomplete.Root` runs `selectionMode: 'none'`, which is what makes free text native: its
 * `value` and `onValueChange` ARE the input string, not a selection, so a typed `09:37 AM` that no
 * slot offers survives perfectly well. Filtering as you type comes free with it, and is strictly
 * better than the legacy field's "scroll the list to the nearest option".
 *
 * ── Typed entry, and its contract ─────────────────────────────────────────────────────────────
 *
 * Inherited wholesale from `DatePicker`, because the two fields sit in the same wizard row and
 * should not behave differently under the same finger. The format is `TIME_PICKER_FORMAT` and
 * nothing else; see the note beside it for why it is 12-hour and why it is not locale-derived.
 *
 *   - Parsing happens on BLUR, never per keystroke. A field that went red on the second character
 *     of every time would be reporting a state the user is still in the middle of leaving.
 *   - An entry that parses and is in range is committed, and the field immediately echoes it back
 *     in canonical form — so `09:30 am` becomes `9:30 AM` and the user can see what was understood.
 *   - An entry that does NOT parse, or that falls before `min`, leaves the committed value
 *     untouched and puts the field in its invalid state, with a sentence saying why (`messages`)
 *     in place of `helperText` until it is fixed. It deliberately does not revert the text:
 *     silently replacing what someone typed with the previous value is the behaviour that makes a
 *     field feel broken, and it also destroys the evidence of what went wrong.
 *   - Clearing commits `null`, from the clear button immediately and from an emptied field on blur.
 *     An empty field is a value, not an error.
 *   - Choosing a slot commits it and closes the list.
 *   - `onValueChange` fires only when the committed TIME actually moves, so a blur with no edit
 *     behind it reports nothing.
 *
 * The component's own invalid state is ORed with the caller's `error`, so the two cannot cancel
 * each other out.
 *
 * Theme is not a prop. Colours resolve from the nearest `.dark` ancestor through the token layer —
 * including for the portalled popup, which gets there via `useInheritedDarkMode`, the same hook
 * `Dialog`, `Select`, `Menu` and `DatePicker` use.
 *
 * `position` is normally supplied by `FieldGroup` rather than set by hand.
 */
export const TimePicker = forwardRef<HTMLInputElement, TimePickerProps>(function TimePicker(
  {
    label,
    value,
    defaultValue,
    onValueChange,
    onEntryErrorChange,
    onBlur,
    min,
    messages,
    helperText,
    error,
    disabled,
    accessibleLabels,
    name,
    required,
    position,
    className,
    rootClassName,
    popupClassName,
    open,
    defaultOpen,
    onOpenChange,
    'data-testid': testId,
  },
  ref
) {
  /**
   * The bordered box, held in STATE rather than in a ref.
   *
   * Both consumers below need the node during a render, and a ref is empty on the one render that
   * matters most: a field mounted with the list already open reads `null` for the theme sentinel
   * and resolves light inside a dark region, with nothing to force the second render that would
   * correct it. A state-backed ref callback re-renders when the node attaches, so the sentinel and
   * the anchor are both live by the time the portal resolves.
   */
  const [box, setBox] = useState<HTMLDivElement | null>(null);

  const [uncontrolledValue, setUncontrolledValue] = useState<Date | null>(defaultValue ?? null);
  const incoming = value === undefined ? uncontrolledValue : value;
  /**
   * The committed value as of the last render, kept so a change made from OUTSIDE can be noticed.
   *
   * Derived during render rather than in an effect, which is the documented React pattern for
   * "reset some state when a prop changes". An effect would paint one frame of the previous text
   * against the new value, and on a controlled field that frame is visible every time a form
   * resets. The comparison is by TIME rather than by reference because a parent that rebuilds its
   * `Date` on every render — which the only call site does, since `useEventDates` re-bases both
   * times onto the occurrence date — would otherwise wipe the user's in-progress typing on every
   * keystroke elsewhere on the page.
   */
  const [seen, setSeen] = useState<Date | null>(incoming !== null && !isValid(incoming) ? null : incoming);

  /**
   * An invalid `Date` is what a form holds while this field refuses an entry (see
   * `onEntryErrorChange`), so it means "unchanged": the field keeps the value it last accepted and
   * the text it refused, rather than reading it as a new value and wiping both.
   */
  const holdingRefusal = incoming !== null && !isValid(incoming);
  const committed = holdingRefusal ? seen : incoming;

  /**
   * Whether the last render's value was that invalid `Date`. Its going away is a change from outside
   * even when the value that replaces it is the very one this field last accepted - a form reset
   * back to it, say - and the refused text has to go with it, or it would sit over a valid form.
   */
  const [heldRefusal, setHeldRefusal] = useState(holdingRefusal);
  const refusalReleased = heldRefusal && !holdingRefusal;

  if (heldRefusal !== holdingRefusal) {
    setHeldRefusal(holdingRefusal);
  }

  const [text, setText] = useState(() => toText(committed));
  const [entryError, setEntryError] = useState<TimePickerEntryError | null>(null);
  const typedInvalid = entryError !== null;

  if (!sameTime(seen, committed) || refusalReleased) {
    setSeen(committed);
    setText(toText(committed));
    setEntryError(null);
  }

  const [uncontrolledOpen, setUncontrolledOpen] = useState(Boolean(defaultOpen));
  const isOpen = open ?? uncontrolledOpen;

  /**
   * The popup is PORTALLED into `<body>`, so it has none of the field's ancestors and the token
   * layer — which resolves colour from position in the DOM — would open a light list on a dark
   * page. The box is a real node at this component's place in the tree, so it doubles as the theme
   * sentinel exactly as `Menu`'s anchor does, and no hidden sentinel element is needed.
   */
  const { inheritedDarkClass } = useInheritedDarkMode(isOpen, box);

  /**
   * The rows, rebuilt only when the BOUND moves.
   *
   * `boundMinutes` reduces `min` to its minute of day, which is the only part of it this field
   * reads (KTD4) and also the only part it can safely memoise on: the call site rebuilds
   * `addMinutes(startTime, 15)` on every render, so a dependency on the `Date`'s identity would
   * re-derive 96 rows and hand Base UI a fresh array each time. Everything below takes the number,
   * so nothing below is capable of comparing instants by accident.
   */
  const minMinutes = boundMinutes(min);
  const slots = useMemo<TimeSlot[]>(() => selectableTimeSlots(minMinutes), [minMinutes]);

  /** What Base UI matches an item by, and what it writes into the input when one is pressed. */
  const itemLabels = useMemo(() => slots.map(({ label: slotLabel }) => slotLabel), [slots]);

  /**
   * Every change to the refusal goes through here, so a caller hears about it the moment it
   * happens and in order with `onValueChange` - which a form needs to stop a submit carrying the
   * last committed value while the field shows text it refused. The render-time reset above does
   * NOT report: it runs because the caller changed the value itself, so the caller already knows.
   */
  const reportEntryError = useCallback(
    (next: TimePickerEntryError | null, accepted: Date | null) => {
      setEntryError(next);

      if (next !== entryError) {
        onEntryErrorChange?.(next, accepted);
      }
    },
    [entryError, onEntryErrorChange]
  );

  const commit = useCallback(
    (next: Date | null) => {
      setUncontrolledValue(next);
      setSeen(next);
      setText(toText(next));
      reportEntryError(null, next);

      // Only a CHANGE is worth reporting. Everything above runs either way — re-typing the time
      // that is already committed still has to canonicalise the text and clear a parse failure —
      // but focusing a field and leaving it without an edit is not a value change, and a caller
      // that marks its form dirty on every notification would say so on a blur nobody typed into.
      if (!sameTime(committed, next)) {
        onValueChange?.(next);
      }
    },
    [committed, onValueChange, reportEntryError]
  );

  /**
   * Minutes to a committed `Date`.
   *
   * The DAY comes from whatever the field already holds, so editing a time leaves the date alone;
   * with nothing held there is nothing to preserve and today is the only sensible answer. The one
   * call site re-bases both times onto the occurrence date afterwards either way, so this matters
   * less there than it would to a caller storing the value as-is.
   */
  const commitMinutes = useCallback(
    (minutes: number) => commit(atTimeOfDay(committed ?? new Date(), minutes)),
    [commit, committed]
  );

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setUncontrolledOpen(nextOpen);
      onOpenChange?.(nextOpen);
    },
    [onOpenChange]
  );

  /**
   * Whether the value in the field was settled by a GESTURE rather than by typing.
   *
   * A ref rather than state, because nothing renders from it and it has to be readable by a blur
   * that may arrive in the same tick as the gesture that set it. See `handleBlur` for what it is
   * for; it is set by `handleValueChange` below, which runs on every keystroke too, so typing
   * always clears it again.
   */
  const settledByGesture = useRef(false);

  /**
   * Every change to the input string, from whichever source.
   *
   * `eventDetails.reason` is the only thing that distinguishes them, and the three that matter are
   * Base UI's own exported string literals (`internals/reason-parts`), not spellings invented here:
   *
   *   `item-press`   a row was chosen, by pointer or by Enter. Base UI has already written the
   *                  row's label into the input; this turns it into a committed value and closes
   *                  the list. Looked up by label rather than re-parsed, so a row can only ever
   *                  commit the time it was generated from.
   *   `clear-press`  the clear button. An explicit gesture with an unambiguous meaning, so it
   *                  commits `null` immediately rather than waiting for a blur — the field being
   *                  replaced clears on the press too.
   *   anything else  typing, pasting, an IME composition. Text only; the parse waits for blur.
   */
  const handleValueChange = useCallback(
    (next: string, eventDetails: { reason: string }) => {
      setText(next);

      const gesture = eventDetails.reason === 'item-press' || eventDetails.reason === 'clear-press';

      settledByGesture.current = gesture;

      if (eventDetails.reason === 'clear-press') {
        commit(null);

        return;
      }

      if (!gesture) {
        return;
      }

      const chosen = slots.find(({ label: slotLabel }) => slotLabel === next);

      if (chosen !== undefined) {
        commitMinutes(chosen.minutes);
      }

      handleOpenChange(false);
    },
    [commit, commitMinutes, handleOpenChange, slots]
  );

  /**
   * The whole of the parse contract, run once, on blur.
   *
   * ── Why a blur that follows a pick or a clear is not parsed ──────────────────────────────────
   *
   * Because the value is already settled, and re-deciding it from `text` would be deciding it from
   * a STALE copy. Both gestures commit synchronously inside `handleValueChange`, and a blur
   * arriving in the same tick still closes over the previous render's `text` — which after a pick
   * from an empty field is `''`, so the parse would read the field as emptied and commit `null`
   * straight over the time the user just chose.
   *
   * Base UI goes to some trouble to make sure this cannot happen: `ComboboxItem` prevents the
   * default on `pointerdown` and on `mousedown` precisely so pressing a row does not blur the
   * input. That holds in a real browser, and it does NOT hold under happy-dom plus `user-event`,
   * where the press lands focus on `<body>`. Both the workaround and the reason it was needed are
   * recorded here rather than left as a test-only quirk: the ordering is genuinely racy, a future
   * Base UI change could make it racy in a browser too, and skipping the parse is correct in both
   * worlds — a picked slot is already canonical, so there is nothing for a parse to add.
   *
   * `onBlur` still fires. A blur happened, and a caller marking a field touched needs to know that
   * whatever settled the value.
   */
  const handleBlur = useCallback(() => {
    // Fired first and unconditionally, because a blur happened whatever the typed entry parses to.
    // A caller using this to mark a field touched must not have that depend on the parse.
    onBlur?.();

    if (settledByGesture.current) {
      settledByGesture.current = false;

      return;
    }

    const entry = text.trim();

    if (entry === '') {
      commit(null);

      return;
    }

    const minutes = parseTimeOfDay(entry);

    if (minutes === null) {
      reportEntryError('format', committed);

      return;
    }

    // The SAME predicate the list is built from, which is the whole point of `slots.ts`.
    if (!isTimeSelectable(minutes, minMinutes)) {
      reportEntryError('beforeMin', committed);

      return;
    }

    commitMinutes(minutes);
  }, [commit, commitMinutes, committed, minMinutes, onBlur, reportEntryError, text]);

  /**
   * An entry refused only for falling before `min` is re-read when `min` moves.
   *
   * The wizard lifts the start field's bound when an occurrence moves off today, and the user has
   * already left this field by then, so no blur is coming to re-read it. Without this the field
   * would stay refused over a time it now accepts, and the form would keep blocking the save. An
   * effect rather than a render-time reset, because the outcome has to reach the caller through
   * `onEntryErrorChange` and `onValueChange`, which are side effects.
   */
  const previousMinMinutes = useRef(minMinutes);

  useEffect(() => {
    if (previousMinMinutes.current === minMinutes) {
      return;
    }

    previousMinMinutes.current = minMinutes;

    if (entryError !== 'beforeMin') {
      return;
    }

    const minutes = parseTimeOfDay(text.trim());

    if (minutes !== null && isTimeSelectable(minutes, minMinutes)) {
      commitMinutes(minutes);
    }
  }, [commitMinutes, entryError, minMinutes, text]);

  // Text that still reads as the committed time is the field showing its value, not a query, so
  // filtering on it would hide every other slot until the field was cleared.
  const showingCommitted = committed !== null && text === toText(committed);

  const labelFor = { ...DEFAULT_ACCESSIBLE_LABELS, ...accessibleLabels };
  const invalid = Boolean(error) || typedInvalid;
  const sentences = { ...DEFAULT_TIME_PICKER_MESSAGES, ...messages };

  // `min` can be gone for the one render before the effect above re-reads a `beforeMin` entry.
  const entryMessage =
    entryError === 'format'
      ? sentences.format(TIME_PICKER_PLACEHOLDER, format(new Date(2026, 0, 15, 9, 30), TIME_PICKER_FORMAT))
      : entryError === 'beforeMin' && min !== undefined
        ? sentences.beforeMin(format(min, TIME_PICKER_FORMAT))
        : null;
  const shownHelper = entryMessage ?? helperText;
  const hasHelper = Boolean(shownHelper);

  return (
    /* `invalid` is what puts `data-invalid` on the root and `aria-invalid` on the control; every red
       token below keys off it, so the visual and the announced state cannot drift apart.

       `Field.Root` is the OUTER element, unlike `DatePicker`, where `Popover.Root` is. That is not a
       preference: `Autocomplete.Root` reads the field context to report `filled` and `disabled` back
       to it, so with the nesting the other way round the label would never shrink. */
    <Field.Root
      className={cn(timePickerRootRecipe(), rootClassName)}
      data-testid={testId}
      disabled={disabled}
      invalid={invalid}
    >
      <Autocomplete.Root
        /**
         * Base UI's open state is CONTROLLED from here even when the caller leaves `open` unset,
         * and that is load-bearing rather than tidiness: choosing a slot has to close the list, and
         * a popup managing its own state cannot be closed from outside it. `uncontrolledOpen` is
         * the fallback the caller would otherwise have got, so an uncontrolled `TimePicker` behaves
         * exactly as one would expect — `Select` and `DatePicker` keep their own open state for the
         * same reason.
         */
        disabled={disabled}
        filter={matchesTimeQuery}
        filteredItems={showingCommitted ? itemLabels : undefined}
        items={itemLabels}
        open={isOpen}
        /**
         * Base UI defaults this to `false`, so the list would open only on Down or on typing. The
         * field being replaced opens on a click, and a combobox whose list a pointer user cannot
         * reach is a text field with a decorative caret.
         */
        openOnInputClick
        value={text}
        onOpenChange={handleOpenChange}
        onValueChange={handleValueChange}
      >
        {/* `data-part` names the bordered box so a parent can target it and a test can assert on it
            without depending on a styling class. The slot classes sit on the root. */}
        <div ref={setBox} className={cn(timePickerBoxRecipe({ position, hasHelper }), className)} data-part="box">
          {/* The control is the FIRST child and pays all the padding itself, so the input fills the
              box and the entire field is a click target — see `TimePicker.recipe`. */}
          <Autocomplete.Input
            ref={ref}
            autoComplete="off"
            className={timePickerControlRecipe({ hasHelper })}
            name={name}
            placeholder={TIME_PICKER_PLACEHOLDER}
            required={required}
            onBlur={handleBlur}
          />

          {/* Always drawn. This field floats its label rather than hiding it once filled - see the
              note in `Inputs/DatePicker/toDsProps` for the sibling field that hid it and why that
              turned out to be a defect rather than a feature. */}
          <Field.Label className={timePickerLabelRecipe()}>
            {label}
            {/* Visual only; the input's native `required` is what a screen reader announces. */}
            {required && (
              <span aria-hidden="true" className={timePickerRequiredMarkRecipe()} data-part="required-mark">
                {' *'}
              </span>
            )}
          </Field.Label>

          {/* KTD8. Base UI mounts it only while the input holds text, so there is no empty-field
              state to style. The `aria-label` is the requirement: the field this replaces renders
              an unnamed icon button here, which a screen reader announces as "button". */}
          <Autocomplete.Clear
            aria-label={labelFor.clear(label)}
            className={timePickerClearRecipe()}
            disabled={disabled}
          >
            <XIcon />
          </Autocomplete.Clear>

          {/* A glyph rather than a button — the input is the combobox, so there is nothing for a
              second focusable element to do. `Autocomplete.Icon` renders it `aria-hidden`. */}
          <Autocomplete.Icon className={timePickerCaretRecipe()}>
            <CaretDownIcon />
          </Autocomplete.Icon>

          {/**
           * Helper text renders inside the border, which is what keeps this field stackable — see
           * `timePickerHelperRecipe`.
           *
           * `Field.Error` needs `match` to render off a caller-supplied flag; without it, it renders
           * only for Base UI's own constraint validation, which this component does not drive.
           * Neither part is a live region — that is Base UI's behaviour rather than a choice made
           * here — so an error that appears after first paint is correctly ASSOCIATED immediately
           * but is not necessarily spoken until the field is next visited. The sibling fields have
           * exactly the same property.
           */}
          {hasHelper && invalid && (
            <Field.Error aria-live="polite" className={timePickerHelperRecipe()} match>
              {shownHelper}
            </Field.Error>
          )}
          {hasHelper && !invalid && (
            <Field.Description className={timePickerHelperRecipe()}>{shownHelper}</Field.Description>
          )}
        </div>

        {/* The theme class goes on the PORTAL, which is an ancestor of the popup — so the popup
            carries only its own `ds-timepicker` slot class and the ordinary cascade reaches it,
            exactly as for a list that never portalled. */}
        <Autocomplete.Portal className={inheritedDarkClass}>
          <Autocomplete.Positioner
            align="start"
            // Anchored to the BOX rather than to the input inside it, so the list lines up with the
            // field a user is looking at and `--anchor-width` measures the border, not the padding
            // box.
            anchor={box}
            className={timePickerPositionerRecipe()}
            side="bottom"
            // 4px of air, the edge gap `Select`, `Menu` and `DatePicker` all settled on, so the
            // four popup families stay in step when collision handling flips them above.
            sideOffset={4}
          >
            <Autocomplete.Popup className={cn(timePickerPopupRecipe(), popupClassName)} data-part="list">
              <Autocomplete.List aria-label={labelFor.list(label)} className={timePickerListRecipe()}>
                {(item: string) => (
                  <Autocomplete.Item key={item} className={timePickerItemRecipe()} value={item}>
                    {item}
                  </Autocomplete.Item>
                )}
              </Autocomplete.List>
            </Autocomplete.Popup>
          </Autocomplete.Positioner>
        </Autocomplete.Portal>
      </Autocomplete.Root>
    </Field.Root>
  );
});
